import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUploadResumeMutation } from "@/hooks/api/profile.queries";
import { useState } from "react";
import { UploadCloud } from "lucide-react";

interface ResumeDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
}

const ResumeDialog = ({
  isOpen,
  onOpenChange,
  onCancel,
}: ResumeDialogProps) => {
  const uploadMutation = useUploadResumeMutation();
  const [file, setFile] = useState<File | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setFile(null);
    }
    onOpenChange(open);
  };

  const cancelAndReset = () => {
    setFile(null);
    onCancel();
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;

    uploadMutation.mutate(file, {
      onSuccess: () => {
        setFile(null);
        onCancel();
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Resume</DialogTitle>
          <DialogDescription>
            Upload a PDF version of your resume to make it available for applications. S3 integration will securely store your file.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-6">
          <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 hover:bg-gray-100 transition">
            <UploadCloud size={40} className="text-gray-400 mb-4" />
            <Input 
              type="file" 
              accept="application/pdf" 
              onChange={handleFileChange}
              className="max-w-xs cursor-pointer"
            />
            <p className="text-sm text-gray-500 mt-2">Only PDF files are supported.</p>
          </div>

          <DialogFooter className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={cancelAndReset}
              disabled={uploadMutation.isPending}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-primary hover:bg-primary/90" 
              disabled={!file || uploadMutation.isPending}
            >
              {uploadMutation.isPending ? "Uploading..." : "Upload Resume"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ResumeDialog;
